import { MdKeyboardArrowRight, MdKeyboardArrowLeft } from 'react-icons/md';
import { Clickable, ClickableHTMLProps } from 'reakit/Clickable';
import cn from 'classnames';

import { usePagination, DOTS } from '@/hooks/usePagination';

interface PaginationProps {
  onPageChange: (number: number) => void;
  totalCount: number;
  siblingCount?: number;
  currentPage: number;
  pageSize: number;
}

type NavigationButtonProps = {
  className?: string;
  onClick: () => void;
  direction: 'prev' | 'next';
};

export function NavigationButton({
  onClick,
  className,
  direction,
}: NavigationButtonProps) {
  const Icon =
    direction === `prev` ? MdKeyboardArrowLeft : MdKeyboardArrowRight;
  const text = direction === `prev` ? `Previous` : `Next`;

  return (
    <Clickable
      as="li"
      onClick={onClick}
      className={cn(
        `inline-flex h-9 w-9 cursor-pointer items-center text-sm font-medium text-zodiac`,
        direction === `next` && `justify-end`,
        className,
      )}
    >
      <Icon className={cn(`h-5 w-5`)} aria-hidden="true" />
      <span className="sr-only">{text}</span>
    </Clickable>
  );
}

type NavigationPageProps = ClickableHTMLProps & {
  isCurrentPage?: boolean;
};

export function NavigationPage({
  onClick,
  children,
  isCurrentPage,
  className,
  ...props
}: NavigationPageProps) {
  return (
    <li className="md:-mt-px">
      <Clickable
        as="span"
        className={cn(
          `inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-transparent px-4 text-sm font-medium transition-all`,
          {
            'text-zodiac hover:bg-zodiac hover:text-white': !isCurrentPage,
            'bg-zodiac text-white ': isCurrentPage,
          },
          className,
        )}
        onClick={onClick}
        {...props}
      >
        {children}
      </Clickable>
    </li>
  );
}

export default function Pagination({
  onPageChange,
  totalCount,
  siblingCount = 1,
  currentPage,
  pageSize,
}: PaginationProps) {
  const paginationRange = usePagination({
    currentPage,
    totalCount,
    siblingCount,
    pageSize,
  });

  if (currentPage === 0 || paginationRange.length === 1) {
    return null;
  }

  const onNext = () => {
    onPageChange(currentPage + 1);
  };

  const onPrevious = () => {
    onPageChange(currentPage - 1);
  };

  const lastPage = paginationRange[paginationRange.length - 1];

  return (
    <nav className="flex justify-center">
      <ul className="flex items-center space-x-2">
        <NavigationButton
          direction="prev"
          onClick={onPrevious}
          className={cn(currentPage === 1 && `md:hidden`)}
        />

        {paginationRange.map((pageNumber, index) => {
          if (pageNumber === DOTS) {
            return (
              <li className="hidden md:-mt-px md:flex" key={index}>
                <span className="inline-flex items-center px-4 text-sm font-medium text-zodiac">
                  &#8230;
                </span>
              </li>
            );
          }

          const isCurrentPage = pageNumber === currentPage;

          return (
            <NavigationPage
              onClick={() => onPageChange(Number(pageNumber))}
              aria-current={isCurrentPage ? `page` : undefined}
              isCurrentPage={isCurrentPage}
              className="hidden md:flex"
              key={index}
            >
              {pageNumber}
            </NavigationPage>
          );
        })}

        <NavigationButton
          direction="next"
          onClick={onNext}
          className={cn(currentPage === lastPage && `md:hidden`)}
        />
      </ul>
    </nav>
  );
}
